import { useState } from "react";

import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { Button } from "~/components/ui/button";
import { PullRequestMarkdown } from "../PullRequestMarkdown";

export interface PullRequestDescriptionEditorProps {
  readonly body: string;
  readonly editable: boolean;
  readonly pending: boolean;
  readonly onSave: (body: string) => Promise<void>;
}

export function PullRequestDescriptionEditor(props: PullRequestDescriptionEditorProps) {
  const editor = usePullRequestDescriptionEditor(props);
  return editor.editing ? (
    <PullRequestDescriptionForm {...editor} pending={props.pending} />
  ) : (
    <PullRequestDescriptionDisplay
      body={props.body}
      editable={props.editable}
      pending={props.pending}
      onEdit={editor.startEditing}
    />
  );
}

function usePullRequestDescriptionEditor({ body, onSave }: PullRequestDescriptionEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);
  const [error, setError] = useState<string | null>(null);
  const startEditing = () => {
    setDraft(body);
    setError(null);
    setEditing(true);
  };
  const save = async () => {
    if (draft === body) {
      setEditing(false);
      return;
    }
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      setError("Couldn’t save the description. Your draft is still here—try again.");
    }
  };
  return { draft, editing, error, setDraft, save, startEditing, cancel: () => setEditing(false) };
}

function PullRequestDescriptionDisplay({
  body,
  editable,
  pending,
  onEdit,
}: {
  readonly body: string;
  readonly editable: boolean;
  readonly pending: boolean;
  readonly onEdit: () => void;
}) {
  return (
    <div className="group relative">
      <PullRequestMarkdown
        text={body.trim().length > 0 ? body : "_No description provided._"}
        className="pr-12"
      />
      {editable ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={pending}
          className="absolute right-0 top-0 opacity-100 md:opacity-0 md:group-hover:opacity-100"
          onClick={onEdit}
        >
          Edit
        </Button>
      ) : null}
    </div>
  );
}

function PullRequestDescriptionForm({
  draft,
  error,
  pending,
  setDraft,
  save,
  cancel,
}: {
  readonly draft: string;
  readonly error: string | null;
  readonly pending: boolean;
  readonly setDraft: (value: string) => void;
  readonly save: () => Promise<void>;
  readonly cancel: () => void;
}) {
  return (
    <div>
      <MarkdownEditor
        value={draft}
        onChange={setDraft}
        ariaLabel="Pull request description"
        placeholder="Add a description…"
        autoFocus
        maxLength={65_536}
        disabled={pending}
      />
      <div className="mt-2 flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={pending || draft.length > 65_536}
          onClick={() => void save()}
        >
          {pending ? "Saving…" : "Save description"}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={pending} onClick={cancel}>
          Cancel
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
